import type { Covering, PoiCoverage } from '../api/types';

/*
 * The arithmetic behind the Coverage tab, with no React in it.
 *
 * Kept apart from the panel because these are the numbers somebody will act
 * on — "nobody has been to four of these" is a decision, not a decoration —
 * and because counting them wrongly is quiet. A place that a round trip
 * starts and ends at is ONE place; a plan whose own line passes it twice has
 * covered it ONCE; an operation that goes back on Thursday has covered it
 * twice. Each of those is a different question, and the shapes below keep
 * them apart rather than hiding them all under the word "stops".
 *
 * Three words are used strictly:
 *   place       — a distinct POI.
 *   occurrence  — one place in one saved plan on one day.
 *   record      — one saved route, or one saved operation entire.
 *
 * "Before" is by PLANNED date, not by when somebody pressed Save: a day
 * planned for the 9th covers ground before a day planned for the 13th, and
 * it makes no difference which was typed in first. Saved work for this round's
 * own date or later is kept apart as `later` — it is not a place anybody has
 * been, it is two plans wanting the same place, which is worth knowing and is
 * not the same thing.
 */

/**
 * A stop of the round being analysed: a named place, or a dropped pin.
 *
 * A pin has no place id, so it cannot be looked up — it is asked about by
 * where it is, and whatever saved work passes within a few paces of it comes
 * back as a match with the distance attached. Everything downstream counts
 * the two alike, which is why they share a key rather than an id.
 */
export interface TrackedPlace {
  /** What the coverage was asked with: the place id, or "pin:3". */
  key: string;
  /** Empty for a pin. */
  poiId: string;
  name: string;
  /** A pin is matched on the ground; a place by identity. */
  kind: 'place' | 'point';
  lat: number;
  lng: number;
  /** Where it sits in `plan.stops`, so the map can be told which one. */
  index: number;
  /**
   * What the map calls it: "S", "E", "S/E" for a round trip's base, or the
   * stop's number. Worked out here rather than in the panel because it
   * depends on the whole round — the last stop is only the end if nothing
   * follows it — and because two lists disagreeing about a stop's number
   * would be worse than neither showing one.
   */
  marker: string;
  /** How many times THIS round stops there — 2 for a round trip's base. */
  stopsHere: number;
}

export interface CoverageFilters {
  /** A `by` value; '' is the unattributed bucket. null means everybody. */
  creator: string | null;
  /** A saved record's id. null means every record. */
  recordId: string | null;
}

export const NO_FILTERS: CoverageFilters = { creator: null, recordId: null };

/** One day of a saved operation, or the whole of a saved route. */
export interface RecordDay {
  /** 1-based for an operation; 0 for a route, which has no days. */
  day: number;
  /** The date it was planned for, or '' where none was set. */
  date: string;
  /** How many stops that day has in all, this round's places or not. */
  planStops: number;
  /** Which of this round's places it covers. */
  places: string[];
  minutes: number;
}

export interface CoverageRecord {
  id: string;
  name: string;
  source: Covering['source'];
  /** Who saved it; '' where the record predates attribution. */
  by: string;
  /** When it was last saved — the newest of its days. */
  at: string | null;
  days: RecordDay[];
  /** Distinct places of this round it covers. */
  places: string[];
  /** Place-days: a place on two days of one operation counts twice. */
  occurrences: number;
  minutes: number;
  /** Its own size: a route's stops, or the sum of an operation's days. */
  planStops: number;
}

export interface CreatorTally {
  by: string;
  /** What to call them, where the account has a name. Empty otherwise. */
  byName: string;
  records: number;
  places: number;
  occurrences: number;
  minutes: number;
  /** Which saved plans those are, so the summary can be read without a
   *  second list beside it. Newest-covering first, as records are. */
  plans: CoverageRecord[];
}

export interface PlaceCoverage {
  place: TrackedPlace;
  /** Coverings planned BEFORE this round, newest first. */
  coverings: Covering[];
  /** Saved work for this round's own date or after it, newest first. */
  later: Covering[];
  /** Distinct records — an operation that goes back twice still counts once. */
  records: number;
  occurrences: number;
  minutes: number;
  lastAt: string | null;
}

export interface CoverageTotals {
  /** Named places in this round. */
  places: number;
  /** Stops in this round that are named places — more than `places` if it
   *  passes one of them twice. */
  stops: number;
  /** Places with at least one covering in scope. */
  covered: number;
  /** Places with none: new ground, as far as the saved record goes. */
  fresh: number;
  occurrences: number;
  /** Planned minutes, summed over the occurrences in scope. */
  minutes: number;
  /** False when every occurrence in scope has no time set against it. */
  minutesKnown: boolean;
  records: number;
  routes: number;
  operations: number;
  /** Places also held by work planned for this date or later. */
  alsoPlanned: number;
  /**
   * The span of saved work these figures rest on, as YYYY-MM-DDTHH:MM.
   *
   * When a plan was made FOR, falling back to the day it was saved. Both ''
   * when nothing matched. Shown because "12 of 12 covered" means something
   * different if the record starts last week than if it starts last year,
   * and the screen is the only place that can say which.
   */
  earliest: string;
  latest: string;
}

export interface CoverageModel {
  places: PlaceCoverage[];
  /** Records to list: narrowed by the creator filter, not by itself. */
  records: CoverageRecord[];
  /** Creators to list: narrowed by the record filter, not by themselves. */
  creators: CreatorTally[];
  totals: CoverageTotals;
}

/**
 * The stops of a round that can be asked about, each of them once.
 *
 * A named place is held by its id. A pin is held by where it is, rounded to
 * about a metre so that the same pin twice in one round — a round trip's
 * base — is one stop and not two, exactly as a repeated place is.
 */
/** What the map prints in a stop's pin: "S", "E", or its number. */
function markerFor(index: number, stops: number): string {
  if (index === 0) return 'S';
  if (index === stops - 1) return 'E';

  return String(index);
}

export function trackedPlaces(
  stops: { poiId: string | null; name: string; lat: number; lng: number }[],
): TrackedPlace[] {
  const out: TrackedPlace[] = [];
  const at = new Map<string, TrackedPlace>();

  stops.forEach((stop, index) => {
    const spot = `${stop.lat.toFixed(5)},${stop.lng.toFixed(5)}`;
    const held = stop.poiId ?? `@${spot}`;

    const seen = at.get(held);
    if (seen) {
      seen.stopsHere += 1;
      // A round trip's base is the start AND the end, and the map says so.
      if (seen.index === 0 && index === stops.length - 1) seen.marker = 'S/E';

      return;
    }

    const place: TrackedPlace = {
      key: stop.poiId ?? `pin:${index}`,
      poiId: stop.poiId ?? '',
      name: stop.name,
      kind: stop.poiId ? 'place' : 'point',
      lat: stop.lat,
      lng: stop.lng,
      index,
      marker: markerFor(index, stops.length),
      stopsHere: 1,
    };
    at.set(held, place);
    out.push(place);
  });

  return out;
}

/**
 * When a covering was FOR, as a plain YYYY-MM-DD.
 *
 * The planned date where the record kept one; otherwise the day it was
 * saved, which is the only other date there is. '' when neither is known,
 * and an unknown date is treated as earlier rather than dropped: the work
 * exists, and hiding it would be the worse mistake of the two.
 */
export function coveringDate(covering: Covering): string {
  if (covering.planDate) return covering.planDate;

  return covering.at ? covering.at.slice(0, 10) : '';
}

/**
 * The same, to the hour: "2026-10-09T09:03".
 *
 * A day is a coarse way to ask which came first. Two plans for one date are
 * not the same claim on the ground if one is due at nine and the other at
 * four, and every planned stop carries the hour it is due. Midnight stands
 * in where a plan sets no time, which puts an undated hour first — the same
 * direction as the rest of this file leans: show the work rather than hide
 * it.
 */
export function coveringAt(covering: Covering): string {
  const day = coveringDate(covering);
  if (!day) return '';

  return `${day}T${covering.planTime || '00:00'}`;
}

/** Whether that covering was planned before this round sets off. */
function isBefore(covering: Covering, roundAt: string): boolean {
  if (!roundAt) return true;
  const when = coveringAt(covering);

  return !when || when < roundAt;
}

/** The newer of two save times, either of which may be missing. */
function later(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;

  return a > b ? a : b;
}

/** Groups coverings into records, one entry per saved route or operation. */
function recordsOf(rows: { poiId: string; covering: Covering }[]): CoverageRecord[] {
  const byId = new Map<string, CoverageRecord>();
  const dayOf = new Map<string, Map<number, RecordDay>>();
  const placesOf = new Map<string, Set<string>>();

  for (const { poiId, covering: c } of rows) {
    let record = byId.get(c.id);
    if (!record) {
      record = {
        id: c.id,
        name: c.name,
        source: c.source,
        by: c.by,
        at: c.at,
        days: [],
        places: [],
        occurrences: 0,
        minutes: 0,
        planStops: 0,
      };
      byId.set(c.id, record);
      dayOf.set(c.id, new Map());
      placesOf.set(c.id, new Set());
    }

    record.at = later(record.at, c.at);
    record.occurrences += 1;
    record.minutes += c.dwellMinutes;
    placesOf.get(c.id)?.add(poiId);

    const days = dayOf.get(c.id);
    if (!days) continue;
    const day = days.get(c.day);
    if (day) {
      day.places.push(poiId);
      day.minutes += c.dwellMinutes;
    } else {
      days.set(c.day, {
        day: c.day,
        date: c.planDate,
        planStops: c.planStops,
        places: [poiId],
        minutes: c.dwellMinutes,
      });
    }
  }

  for (const record of byId.values()) {
    record.places = [...(placesOf.get(record.id) ?? [])];
    record.days = [...(dayOf.get(record.id)?.values() ?? [])]
      .sort((a, b) => a.day - b.day);
    /*
     * A record's own size is the sum of its days, because that is what a
     * day's `planStops` counts. A route has exactly one "day", so this is
     * simply its stop count.
     */
    record.planStops = record.days.reduce((total, d) => total + d.planStops, 0);
  }

  return [...byId.values()].sort((a, b) => {
    if (b.places.length !== a.places.length) return b.places.length - a.places.length;

    return (b.at ?? '').localeCompare(a.at ?? '');
  });
}

/**
 * One tally per person who saved something in scope, with their plans.
 *
 * The plans ride along because "admin · 3 saved plans" invites exactly one
 * question, and answering it in a second list somewhere else means holding
 * two groupings of the same rows in your head.
 */
function creatorsOf(rows: { poiId: string; covering: Covering }[]): CreatorTally[] {
  const byPerson = new Map<string, CreatorTally & { pois: Set<string> }>();
  const plansOf = new Map<string, CoverageRecord[]>();

  for (const record of recordsOf(rows)) {
    const held = plansOf.get(record.by);
    if (held) held.push(record);
    else plansOf.set(record.by, [record]);
  }

  for (const { poiId, covering: c } of rows) {
    let tally = byPerson.get(c.by);
    if (!tally) {
      tally = {
        by: c.by, byName: c.byName, records: 0, places: 0, occurrences: 0, minutes: 0,
        plans: plansOf.get(c.by) ?? [], pois: new Set(),
      };
      byPerson.set(c.by, tally);
    }
    tally.occurrences += 1;
    tally.minutes += c.dwellMinutes;
    tally.pois.add(poiId);
  }

  return [...byPerson.values()]
    .map(({ pois, ...tally }) => ({
      ...tally, records: tally.plans.length, places: pois.size,
    }))
    .sort((a, b) => {
      if (b.minutes !== a.minutes) return b.minutes - a.minutes;

      return b.occurrences - a.occurrences;
    });
}

/**
 * Everything the panel shows, for one round and one set of filters.
 *
 * The two filters narrow different lists, which is deliberate: picking a
 * person narrows the RECORDS (those are theirs) but must leave the people
 * list alone, or there would be no way to pick somebody else. Picking a
 * record narrows the PEOPLE for the same reason in reverse. The places and
 * the totals answer to both, because they are the thing being filtered.
 */
export function buildCoverage(
  places: TrackedPlace[],
  entries: PoiCoverage[],
  options: {
    filters?: CoverageFilters;
    ignoreRecordId?: string;
    /** This round's own date, YYYY-MM-DD. Without it nothing is "later". */
    roundDate?: string;
    /** The hour it sets off, HH:MM. Midnight when the round does not say. */
    roundTime?: string;
  } = {},
): CoverageModel {
  const filters = options.filters ?? NO_FILTERS;
  const roundAt = options.roundDate
    ? `${options.roundDate}T${options.roundTime || '00:00'}`
    : '';
  const wanted = new Set(places.map((p) => p.key));

  /*
   * Every covering of a place in this round, flattened.
   *
   * The record being looked at is dropped here rather than in the query, so
   * one reply serves a page that has something to exclude and one that does
   * not. Without it, opening a saved round reports every stop as covered —
   * by itself.
   */
  const all: { poiId: string; covering: Covering }[] = [];
  const ahead: { poiId: string; covering: Covering }[] = [];
  for (const entry of entries) {
    const asked = entry.key || entry.poiId;
    if (!wanted.has(asked)) continue;
    for (const covering of entry.coverings) {
      if (options.ignoreRecordId && covering.id === options.ignoreRecordId) continue;
      const row = { poiId: asked, covering };
      if (isBefore(covering, roundAt)) all.push(row);
      else ahead.push(row);
    }
  }

  const byCreator = (row: { covering: Covering }) =>
    filters.creator === null || row.covering.by === filters.creator;
  const byRecord = (row: { covering: Covering }) =>
    filters.recordId === null || row.covering.id === filters.recordId;

  const inScope = all.filter((row) => byCreator(row) && byRecord(row));

  const coveringsFor = new Map<string, Covering[]>();
  for (const { poiId, covering } of inScope) {
    const list = coveringsFor.get(poiId);
    if (list) list.push(covering);
    else coveringsFor.set(poiId, [covering]);
  }

  /* The same two filters apply to work planned for later. */
  const laterFor = new Map<string, Covering[]>();
  for (const row of ahead) {
    if (!byCreator(row) || !byRecord(row)) continue;
    const list = laterFor.get(row.poiId);
    if (list) list.push(row.covering);
    else laterFor.set(row.poiId, [row.covering]);
  }

  const placeRows: PlaceCoverage[] = places.map((place) => {
    const coverings = (coveringsFor.get(place.key) ?? [])
      .slice()
      .sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));

    return {
      place,
      coverings,
      later: (laterFor.get(place.key) ?? [])
        .slice()
        .sort((a, b) => coveringAt(a).localeCompare(coveringAt(b))),
      records: new Set(coverings.map((c) => c.id)).size,
      occurrences: coverings.length,
      minutes: coverings.reduce((total, c) => total + c.dwellMinutes, 0),
      lastAt: coverings.reduce<string | null>((newest, c) => later(newest, c.at), null),
    };
  });

  /* The oldest and newest work the figures below are drawn from. */
  const span = inScope.reduce(
    (found, row) => {
      const when = coveringAt(row.covering);
      if (!when) return found;

      return {
        earliest: !found.earliest || when < found.earliest ? when : found.earliest,
        latest: !found.latest || when > found.latest ? when : found.latest,
      };
    },
    { earliest: '', latest: '' },
  );

  const records = recordsOf(all.filter(byCreator));
  const scopedRecords = recordsOf(inScope);
  const covered = placeRows.filter((p) => p.occurrences > 0).length;

  return {
    places: placeRows,
    records,
    creators: creatorsOf(all.filter(byRecord)),
    totals: {
      places: places.length,
      stops: places.reduce((total, p) => total + p.stopsHere, 0),
      covered,
      fresh: places.length - covered,
      occurrences: inScope.length,
      minutes: inScope.reduce((total, r) => total + r.covering.dwellMinutes, 0),
      minutesKnown: inScope.some((r) => r.covering.dwellMinutes > 0),
      records: scopedRecords.length,
      routes: scopedRecords.filter((r) => r.source === 'route').length,
      operations: scopedRecords.filter((r) => r.source === 'operation').length,
      alsoPlanned: placeRows.filter((p) => p.later.length > 0).length,
      earliest: span.earliest,
      latest: span.latest,
    },
  };
}

/**
 * Where to open a saved record, for somebody who wants to see the thing
 * itself rather than the fact of it.
 *
 * The Saved screen lists only what this browser saved, but it opens a record
 * by id — so a link works even for a round somebody else saved, which is
 * exactly the case coverage creates.
 */
export function planHref(id: string, source: Covering['source'], day = 0): string {
  if (source === 'operation') {
    return `/saved?trip=${encodeURIComponent(id)}${day > 0 ? `&day=${day}` : ''}`;
  }

  return `/saved?route=${encodeURIComponent(id)}`;
}

export type PlaceSort = 'route' | 'most' | 'time' | 'name';
export type PlaceShow = 'all' | 'covered' | 'fresh' | 'later';

/** The place list as the toolbar asks for it: searched, filtered, ordered. */
export function arrangePlaces(
  places: PlaceCoverage[],
  { query = '', show = 'all', sort = 'route' }:
  { query?: string; show?: PlaceShow; sort?: PlaceSort },
): PlaceCoverage[] {
  const needle = query.trim().toLowerCase();

  const kept = places.filter((p) => {
    if (show === 'covered' && p.occurrences === 0) return false;
    if (show === 'fresh' && p.occurrences > 0) return false;
    if (show === 'later' && p.later.length === 0) return false;

    return !needle || p.place.name.toLowerCase().includes(needle);
  });

  /*
   * Sorted on a copy, and always with the round's own order underneath.
   *
   * Two places covered the same number of times would otherwise swap about
   * between renders, which reads as the list moving on its own.
   */
  return kept.slice().sort((a, b) => {
    if (sort === 'most' && b.occurrences !== a.occurrences) {
      return b.occurrences - a.occurrences;
    }
    if (sort === 'time' && b.minutes !== a.minutes) return b.minutes - a.minutes;
    if (sort === 'name') {
      const byName = a.place.name.localeCompare(b.place.name);
      if (byName !== 0) return byName;
    }

    return a.place.index - b.place.index;
  });
}
